import React, { useEffect, useState } from 'react';
import {
    Alert, AlertIcon, Box, Button, Checkbox, Divider, FormControl, FormLabel,
    Grid, HStack, IconButton, Input, Spinner, Text, VStack,
} from '@chakra-ui/react';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';

import { FetchedService, fetchManagedServices } from '../../api/serviceApi';
import {
    createStaffTimeOff, deleteStaffTimeOff, fetchStaffScheduling, StaffScheduling,
    updateStaffScheduling, WorkingDay, WorkingHoursConfig,
} from '../../api/staffApi';
import { UserOut } from '../../types/User';
import { useBrandedToast } from '../../hooks/useBrandedToast';

interface Props {
    staffMember: UserOut;
    onSubmitSuccess: () => void;
    onCancel: () => void;
}

const DAYS = [
    ['monday', 'Lundi'], ['tuesday', 'Mardi'], ['wednesday', 'Mercredi'], ['thursday', 'Jeudi'],
    ['friday', 'Vendredi'], ['saturday', 'Samedi'], ['sunday', 'Dimanche'],
] as const;

const defaultHours = (): WorkingHoursConfig => Object.fromEntries(
    DAYS.map(([key]) => [key, { isOpen: key !== 'sunday', intervals: [{ start: '09:00', end: '18:00' }] }])
);

const toLocalInput = (value: string): string => {
    const date = new Date(value);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

const StaffSchedulingForm: React.FC<Props> = ({ staffMember, onSubmitSuccess, onCancel }) => {
    const toast = useBrandedToast();
    const [services, setServices] = useState<FetchedService[]>([]);
    const [scheduling, setScheduling] = useState<StaffScheduling | null>(null);
    const [hours, setHours] = useState<WorkingHoursConfig>(defaultHours());
    const [serviceIds, setServiceIds] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timeOffStart, setTimeOffStart] = useState('');
    const [timeOffEnd, setTimeOffEnd] = useState('');
    const [timeOffReason, setTimeOffReason] = useState('');

    const load = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [loadedScheduling, loadedServices] = await Promise.all([
                fetchStaffScheduling(staffMember.id), fetchManagedServices(),
            ]);
            setScheduling(loadedScheduling);
            setServices(loadedServices);
            setServiceIds(loadedScheduling.service_ids);
            setHours(loadedScheduling.working_hours_config || defaultHours());
        } catch (requestError: any) {
            setError(requestError.response?.data?.detail || 'Impossible de charger les horaires.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { load(); }, [staffMember.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const updateDay = (day: string, update: (current: WorkingDay) => WorkingDay) => {
        setHours((current) => ({ ...current, [day]: update(current[day] || { isOpen: false, intervals: [] }) }));
    };

    const saveSchedule = async () => {
        setIsSaving(true);
        try {
            const updated = await updateStaffScheduling(staffMember.id, { service_ids: serviceIds, working_hours_config: hours });
            setScheduling(updated);
            toast({ title: 'Planning enregistré', status: 'success' });
            onSubmitSuccess();
        } catch (requestError: any) {
            toast({ title: 'Enregistrement impossible', description: requestError.response?.data?.detail || 'Réessayez.', status: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const addTimeOff = async () => {
        if (!timeOffStart || !timeOffEnd) return;
        try {
            await createStaffTimeOff(staffMember.id, {
                starts_at: new Date(timeOffStart).toISOString(),
                ends_at: new Date(timeOffEnd).toISOString(),
                reason: timeOffReason || undefined,
            });
            setTimeOffStart(''); setTimeOffEnd(''); setTimeOffReason('');
            await load();
        } catch (requestError: any) {
            toast({ title: 'Indisponibilité impossible', description: requestError.response?.data?.detail || 'Vérifiez les dates.', status: 'error' });
        }
    };

    const removeTimeOff = async (timeOffId: number) => {
        try {
            await deleteStaffTimeOff(staffMember.id, timeOffId);
            await load();
        } catch {
            toast({ title: 'Suppression impossible', status: 'error' });
        }
    };

    if (isLoading) return <VStack py={10}><Spinner color="brand.500" /><Text fontSize="sm" color="gray.500">Chargement du planning...</Text></VStack>;
    if (error) return <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>;

    return (
        <VStack align="stretch" spacing={6}>
            <Box>
                <Text fontWeight="700" color="gray.900">Services proposés</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>Seuls les services cochés peuvent être réservés avec {staffMember.name}.</Text>
                <VStack align="stretch" spacing={2} mt={3}>
                    {services.map((service) => <Checkbox key={service.id} isChecked={serviceIds.includes(service.id)} onChange={() => setServiceIds((current) => current.includes(service.id) ? current.filter((id) => id !== service.id) : [...current, service.id])}>{service.name} <Text as="span" color="gray.500">({service.duration_minutes} min)</Text></Checkbox>)}
                </VStack>
            </Box>

            <Divider />
            <Box>
                <Text fontWeight="700" color="gray.900">Horaires hebdomadaires</Text>
                <Text fontSize="sm" color="gray.500" mt={1}>Heure de Casablanca. Ajoutez un second créneau pour une pause.</Text>
                <VStack align="stretch" spacing={3} mt={4}>
                    {DAYS.map(([key, label]) => {
                        const day = hours[key] || { isOpen: false, intervals: [] };
                        return (
                            <Box key={key} p={3} borderWidth="1px" borderColor="gray.100" borderRadius="lg">
                                <HStack justify="space-between">
                                    <Checkbox
                                        isChecked={day.isOpen}
                                        onChange={(event) => updateDay(key, (current) => ({
                                            ...current,
                                            isOpen: event.target.checked,
                                            intervals: current.intervals.length ? current.intervals : [{ start: '09:00', end: '18:00' }],
                                        }))}
                                        fontWeight="600"
                                    >
                                        {label}
                                    </Checkbox>
                                    {day.isOpen && day.intervals.length < 2 && (
                                        <Button size="xs" variant="ghost" leftIcon={<Plus size={13} />} onClick={() => updateDay(key, (current) => ({ ...current, intervals: [...current.intervals, { start: '14:00', end: '18:00' }] }))}>
                                            Pause
                                        </Button>
                                    )}
                                </HStack>
                                {day.isOpen && (
                                    <VStack mt={3} align="stretch" spacing={2}>
                                        {day.intervals.map((interval, index) => (
                                            <HStack key={index}>
                                                <Input size="sm" type="time" value={interval.start} onChange={(event) => updateDay(key, (current) => ({ ...current, intervals: current.intervals.map((value, intervalIndex) => intervalIndex === index ? { ...value, start: event.target.value } : value) }))} />
                                                <Text color="gray.400">à</Text>
                                                <Input size="sm" type="time" value={interval.end} onChange={(event) => updateDay(key, (current) => ({ ...current, intervals: current.intervals.map((value, intervalIndex) => intervalIndex === index ? { ...value, end: event.target.value } : value) }))} />
                                                {day.intervals.length > 1 && <IconButton aria-label="Supprimer le créneau" size="sm" variant="ghost" colorScheme="red" icon={<Trash2 size={14} />} onClick={() => updateDay(key, (current) => ({ ...current, intervals: current.intervals.filter((_, intervalIndex) => intervalIndex !== index) }))} />}
                                            </HStack>
                                        ))}
                                    </VStack>
                                )}
                            </Box>
                        );
                    })}
                </VStack>
                <Button mt={4} colorScheme="brand" onClick={saveSchedule} isLoading={isSaving}>Enregistrer le planning</Button>
            </Box>

            <Divider />
            <Box>
                <HStack spacing={2}><CalendarOff size={16} /><Text fontWeight="700" color="gray.900">Indisponibilités</Text></HStack>
                <Grid mt={3} templateColumns={{ base: '1fr', sm: '1fr 1fr' }} gap={3}><FormControl><FormLabel fontSize="xs">Début</FormLabel><Input type="datetime-local" value={timeOffStart} onChange={(event) => setTimeOffStart(event.target.value)} /></FormControl><FormControl><FormLabel fontSize="xs">Fin</FormLabel><Input type="datetime-local" value={timeOffEnd} onChange={(event) => setTimeOffEnd(event.target.value)} /></FormControl></Grid>
                <Input mt={3} placeholder="Motif (optionnel)" value={timeOffReason} onChange={(event) => setTimeOffReason(event.target.value)} />
                <Button mt={3} variant="outline" onClick={addTimeOff} isDisabled={!timeOffStart || !timeOffEnd}>Ajouter l&apos;indisponibilité</Button>
                <VStack align="stretch" spacing={2} mt={4}>{scheduling?.time_off.length ? scheduling.time_off.map((entry) => <HStack key={entry.id} justify="space-between" p={3} bg="gray.50" borderRadius="lg"><Box><Text fontSize="sm" fontWeight="600">{new Date(entry.starts_at).toLocaleString('fr-FR')} - {new Date(entry.ends_at).toLocaleString('fr-FR')}</Text>{entry.reason && <Text fontSize="xs" color="gray.500">{entry.reason}</Text>}</Box><IconButton aria-label="Supprimer l'indisponibilité" size="sm" variant="ghost" colorScheme="red" icon={<Trash2 size={15} />} onClick={() => removeTimeOff(entry.id)} /></HStack>) : <Text fontSize="sm" color="gray.500">Aucune indisponibilité planifiée.</Text>}</VStack>
            </Box>
            <HStack justify="flex-end"><Button variant="ghost" onClick={onCancel}>Fermer</Button></HStack>
        </VStack>
    );
};

export default StaffSchedulingForm;